import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CalendarEvent } from 'angular-calendar';
import { endOfMonth, endOfWeek, isEqual, isSameDay, startOfMonth, startOfWeek } from 'date-fns';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BackendResponse, DateService, DeviceService, HttpService, ScreenSize, TranslateService } from 'vas-mobile-api';

import { SlotDetail, SlotStatus, SlotSummary, SpecialDate } from '../models/loadingSlot';
import { DataExchangeService } from '../services/dataexchange.service';

@Component({
  selector: 'app-loading-slot-visual',
  templateUrl: './loading-slot-visual.component.html',
  styleUrls: ['./loading-slot-visual.component.css']
})
export class LoadingSlotVisualComponent implements OnInit, AfterViewInit, OnDestroy {

  public colors: any = {
    red: {
      primary: '#ad2121',
      secondary: '#FAE3E3'
    },
    red2: {
      primary: '#ba2933'
    },
    blue: {
      primary: '#1e90ff',
      secondary: '#D1E8FF'
    },
    yellow: {
      primary: '#e3bc08',
      secondary: '#FDF1BA'
    },
    green: {
      primary: '#1d8821',
      secondary: '#b8fbba'
    },
    darkbrown: {
      primary: '#532c12'
    }
  };

  public slotForm: FormGroup;
  public view: 'month' | 'week' | 'day' = 'month';

  public viewDate: Date = new Date();
  public mobileViewDate: Date = new Date();

  public refresh: Subject<any> = new Subject();
  public monthEvents: Array<CalendarEvent<Partial<SlotSummary>>> = [];
  public weekEvents: Array<CalendarEvent<SlotDetail>> = [];
  public dayEvents: Array<CalendarEvent<SlotDetail>> = [];
  // public events: CalendarEvent<SlotReservation>[] = [];
  public activeDayIsOpen = false;
  public isMobileView: boolean;
  public firstDayOfWeek = 1;
  private ngUnsubscribe: Subject<any> = new Subject();
  public dayStartHour: number;
  public dayEndHour: number;
  public showLoading = false;
  public locale: string;
  private specialDates: SpecialDate[] = [];

  constructor(
    private http: HttpService,
    private deviceService: DeviceService,
    private des: DataExchangeService,
    private dateService: DateService,
    private translateService: TranslateService,
    private router: Router) { }

  ngOnInit(): void {
    this.locale = this.translateService.getLanguage();

    this.deviceService.size.pipe(takeUntil(this.ngUnsubscribe)).subscribe((size) => {
      this.isMobileView = size <= ScreenSize.sm;
      if (this.isMobileView) {
        this.view = 'day';
      }
    });

    this.slotForm = new FormGroup({
      'day': new FormControl(this.dateService.getDate(new Date()))
    });

    this.slotForm.get('day').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((newValue) => {
      if (newValue) {
        this.mobileViewDate = newValue;
        this.loadSlots('day', newValue, newValue);
      }
    });

    this.des.changeTitelBarName('LABEL_LOADING_SLOT');

  }

  ngAfterViewInit(): void {
    this.http.getSystemParam('FIRSTDAYOFWEEK', false, false).subscribe((response) => {
      this.firstDayOfWeek = response.data.value - 1; // from backend: 1 -> Sunday, here: 0 -> Sunday
    }, () => {
    }, () => {
      this.onViewDateChange(this.viewDate);
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();

  }

  public dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {

    this.viewDate = date;
    this.changeView('day');

  }

  private loadSlots(view: string, dateFrom: Date, dateTo: Date) {
    this.dayEvents = [];
    this.weekEvents = [];
    this.monthEvents = [];
    this.dayStartHour = 0;
    this.dayEndHour = 24;
    this.showLoading = true;
    if (view === 'day') {
      // get day details
      this.getSlotsForDay(dateFrom);
    } else if (view === 'week') {
      this.getSlotsForWeek(dateFrom, dateTo);
      // get details
    } else if (view === 'month') {
      this.getSlotsForMonth(dateFrom, dateTo);
      // get summary
    }

  }

  private getSlotsForDay(date: Date) {
    this.http.get<BackendResponse<SlotDetail[]>>({
      pathId: 'getSlotDetailsForCustomer', urlParams: {
        date: this.dateService.getDate(date)
      }
    }).pipe(map((data) => {
      data.data.forEach((element) => {
        element.start = this.dateService.combineDateAndTime(element.SLOTVON_DATE, element.SLOTVON_TIME);
        element.end = this.dateService.combineDateAndTime(element.SLOTBIS_DATE, element.SLOTBIS_TIME);
      });
      return data.data;
    })).subscribe((response) => {
      this.showLoading = false;
      response.filter((slot, index, self) => {
        // only show one open slot per timespan
        if (slot.SLOT_STATUS === SlotStatus.OPEN) {
          return self.findIndex((s) => {
            return isEqual(s.start, slot.start) &&
              isEqual(s.end, slot.end) &&
              s.SLOT_STATUS === SlotStatus.OPEN &&
              s.PRODUCT === slot.PRODUCT;
          }) === index;
        }
        return true;
      }).forEach((slot) => {
        // this.setStartTimeBoundary(getHours(slot.start));
        // this.setEndTimeBoundary(getHours(slot.end));
        const event = this.buildSlotDetailEvent(slot);
        this.dayEvents.push(event);
      });
      this.refresh.next();
    }, (e) => {
      this.showLoading = false;
    });
    this.getSpecialDates(date);
  }

  private getSlotsForWeek(dateFrom: Date, dateTo: Date) {
    this.http.get<BackendResponse<SlotDetail[]>>({
      pathId: 'getMultipleSlotDetailsForCustomer', urlParams: {
        dateFrom: this.dateService.getDate(dateFrom),
        dateTo: this.dateService.getDate(dateTo)
      }
    }).pipe(map((data) => {
      data.data.forEach((element) => {
        element.start = this.dateService.combineDateAndTime(element.SLOTVON_DATE, element.SLOTVON_TIME);
        element.end = this.dateService.combineDateAndTime(element.SLOTBIS_DATE, element.SLOTBIS_TIME);
      });
      return data.data;
    })).subscribe((response) => {
      this.showLoading = false;
      response.forEach((slot) => {
        const event = this.buildSlotDetailEvent(slot);
        this.weekEvents.push(event);
      });
      this.refresh.next();
    }, (e) => {
      this.showLoading = false;
    });
    this.getSpecialDates(dateFrom, dateTo);
  }

  private getSlotsForMonth(dateFrom: Date, dateTo: Date) {
    this.http.get<BackendResponse<SlotSummary[]>>({
      pathId: 'listSlotSummaryForCustomer', urlParams: {
        dateFrom: this.dateService.getDate(dateFrom),
        dateTo: this.dateService.getDate(dateTo)
      }
    }).subscribe((response) => {
      this.showLoading = false;
      response.data.forEach((slot) => {
        slot.date = new Date(slot.date);
        const event: CalendarEvent<SlotSummary> = {
          start: slot.date,
          // end: slot.date,
          title: '',
          color: this.colors.red,
          meta: slot
        };
        this.monthEvents.push(event);
      });
      this.refresh.next();
    }, (e) => {
      this.showLoading = false;
    });
  }

  private getSpecialDates(dateFrom: Date, dateTo?: Date): void {
    this.http.get<BackendResponse<SpecialDate[]>>({
      pathId: 'getSpecialDateObjects',
      urlParams: {
        dateFrom: this.dateService.getDate(dateFrom),
        dateTo: dateTo ? this.dateService.getDate(dateTo) : null
      }
    }).subscribe((result) => {
      this.specialDates = result.data;
    });
  }

  public getSpecialDateByDate(date: Date) {
    return this.specialDates.find((d) => isSameDay(d.SPECIAL_DATE, date));
  }

  private buildSlotDetailEvent(slot: SlotDetail): CalendarEvent<SlotDetail> {
    let eventColor: any;
    let eventTitle: string;
    switch (slot.SLOT_STATUS) {
      case SlotStatus.OPEN:
        eventColor = this.colors.green;
        eventTitle = this.arrayToString(slot.MATERIALS);
        break;
      case SlotStatus.BOOKED:
        eventColor = this.colors.blue;
        eventTitle = slot.KFZ_KENNZEICHEN;
        break;
      case SlotStatus.ERROR:
        eventColor = this.colors.red;
        eventTitle = slot.KFZ_KENNZEICHEN;
        break;
    }
    const event: CalendarEvent<SlotDetail> = {
      start: slot.start,
      end: slot.end,
      title: eventTitle,
      color: eventColor,
      meta: slot
    };
    return event;
  }

  private arrayToString(array: string[]): string {
    let result = '';
    if (array && array.length > 0) {
      array.forEach((a) => {
        result += a + ', ';
      });
      result = result.substring(0, result.length - 3);
    }
    return result;
  }

  public onViewDateChange(date: Date) {
    let startDate: Date;
    let endDate: Date;
    switch (this.view) {
      case 'month':
        startDate = startOfWeek(startOfMonth(date), { weekStartsOn: this.firstDayOfWeek });
        endDate = endOfWeek(endOfMonth(date), { weekStartsOn: this.firstDayOfWeek });
        break;
      case 'week':
        startDate = startOfWeek(date, { weekStartsOn: this.firstDayOfWeek });
        endDate = endOfWeek(date, { weekStartsOn: this.firstDayOfWeek });
        break;
      case 'day':
        startDate = date;
        endDate = date;
        break;
    }
    this.loadSlots(this.view, startDate, endDate);
  }

  public changeView(view: 'month' | 'week' | 'day') {
    if (this.view !== view) {
      this.view = view;
      this.onViewDateChange(this.viewDate);
    }
  }

  private setStartTimeBoundary(hour: number) {
    if (this.dayStartHour == null || this.dayStartHour > hour) {
      this.dayStartHour = hour;
    }
  }

  private setEndTimeBoundary(hour: number) {
    if (this.dayEndHour == null || this.dayEndHour < hour) {
      this.dayEndHour = hour;
    }
  }

  public getSpecialDayTooltip(input: CalendarEvent<SlotSummary> | SpecialDate) {
    if (this.isEvent(input)) {
      if (input.meta.isClosed) {
        return this.translateService.translateFirstLetterUpperCase('LBLPANEL_SLOTCLOSED');
      }
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:CLOSED_FROM_TO%1%2', input.meta.specialTimeAb, input.meta.specialTimeBis);
    } else {
      if (input.GESCHLOSSEN_FLAG) {
        return this.translateService.translateFirstLetterUpperCase('LBLPANEL_SLOTCLOSED');
      }
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:CLOSED_FROM_TO%1%2', input.OFFENAB_TIME, input.OFFENBIS_TIME);
    }
  }

  public getOpenTooltip(input: CalendarEvent<SlotSummary> | number) {
    if (typeof input === 'number') {
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:FREE_SLOTS%1', input.toString());
    } else {
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:FREE_SLOTS%1', input.meta.open.toString());
    }
  }

  public getBookedTooltip(input: CalendarEvent<SlotSummary> | number) {
    if (typeof input === 'number') {
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:BOOKED_SLOTS%1', input.toString());
    } else {
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:BOOKED_SLOTS%1', input.meta.booked.toString());
    }
  }

  public getErrorTooltip(input: CalendarEvent<SlotSummary> | number) {
    if (typeof input === 'number') {
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:FLAWED_SLOTS%1', input.toString());
    } else {
      return this.translateService.translateFirstLetterUpperCase('MESSAGE:FLAWED_SLOTS%1', input.meta.error.toString());
    }
  }

  private isEvent(object: any): object is CalendarEvent {
    return 'meta' in object;
  }

  getWeekEventsByDay(date: Date): Array<CalendarEvent<SlotDetail>> {
    return this.weekEvents.filter((event) => {
      return isSameDay(event.start, date);
    });
  }

  getSum(events: Array<CalendarEvent<SlotDetail>>, status: string): number {
    return events.filter((e) => {
      return e.meta.SLOT_STATUS === status;
    }).length;
  }

  openDelivery(event: CalendarEvent<SlotDetail>) {
    if (event.meta.LIEFER_NR) {
      this.router.navigate(['deliveryDetail', event.meta.LIEFER_NR.trim()]);
    }
  }

}
